import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type FixtureCreationStatus,
  type ScenarioGroupSummary,
  useCreateFixtureFromScenarioGroupMutation,
  useLazyGetFixtureCreationStatusQuery,
  useListScenarioGroupsQuery,
} from '../api/promptTestingApi';

const MAX_STATUS_POLL_FAILURES = 3;

interface CompletedSummary {
  fixtureSetId: string;
  sessionCount: number;
  scenarioGroupSlug?: string;
  anonymizationStatus?: string;
}

export function buildScenarioFixtureSeed(groupSlug: string, profileSlug: string): string {
  return `${profileSlug.trim()}:${groupSlug.trim()}:beta-v1`;
}

export function clampDocumentCount(value: number, group: ScenarioGroupSummary): number {
  if (Number.isNaN(value)) return group.default_documents;
  return Math.min(Math.max(value, group.min_documents), group.max_documents);
}

export function isScenarioCompatible(group: ScenarioGroupSummary, profileSlug: string): boolean {
  return group.compatible_profiles.length === 0 || group.compatible_profiles.includes(profileSlug);
}

export function fixtureCreationFailureHelp(
  errorCode?: FixtureCreationStatus['error_code']
): string | null {
  switch (errorCode) {
    case 'insufficient_candidates':
      return 'Try a smaller persona size in Advanced options, choose a broader scenario, or add more eligible session data.';
    case 'anonymization_failed':
      return 'No persona was saved. Choose another scenario or ask an admin to inspect the anonymization report.';
    case 'content_unavailable':
      return 'Choose another scenario or ask an admin to refresh transcript ingestion for these sessions.';
    case 'transcript_access_denied':
      return 'Choose another scenario whose transcripts are accessible to your account.';
    case 'service_unavailable':
      return 'The anonymizer readiness gate is blocking creation. Ask an admin to check anonymizer /readyz.';
    case 'internal':
      return 'No persona was saved. Check backend logs and try again.';
    default:
      return null;
  }
}

export function extractFixtureCreationError(err: unknown): string {
  return (
    (err as { data?: { error?: string } })?.data?.error ??
    (err as { message?: string })?.message ??
    'Unknown error'
  );
}

export function extractFixtureCreationErrorCode(
  err: unknown
): FixtureCreationStatus['error_code'] | undefined {
  return (err as { data?: { error_code?: FixtureCreationStatus['error_code'] } })?.data?.error_code;
}

interface FixtureCreationWizardProps {
  open: boolean;
  profileSlug: string;
  onClose: () => void;
  onCreated?: (fixtureSetId: string) => void;
}

export function FixtureCreationWizard({
  open,
  profileSlug,
  onClose,
  onCreated,
}: FixtureCreationWizardProps) {
  const [selectedGroupSlug, setSelectedGroupSlug] = useState<string | null>(null);
  const [documentCount, setDocumentCount] = useState(0);
  const [fixtureName, setFixtureName] = useState('');
  const [fixtureDescription, setFixtureDescription] = useState('');
  const [jobID, setJobID] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [creationStatus, setCreationStatus] = useState<{
    pass: number;
    passName: string;
    total: number;
  } | null>(null);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [creationErrorCode, setCreationErrorCode] = useState<
    FixtureCreationStatus['error_code'] | undefined
  >(undefined);
  const [completed, setCompleted] = useState(false);
  const [completedSummary, setCompletedSummary] = useState<CompletedSummary | null>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const notifiedFixtureSetIdRef = useRef<string | null>(null);
  const statusPollFailuresRef = useRef(0);

  const {
    data: scenarioGroupsData,
    isLoading: isLoadingScenarioGroups,
    isError: isScenarioGroupsError,
  } = useListScenarioGroupsQuery(undefined, { skip: !open });
  const [createFromScenarioGroup] = useCreateFixtureFromScenarioGroupMutation();
  const [fetchStatus] = useLazyGetFixtureCreationStatusQuery();
  const scenarioGroups = scenarioGroupsData?.groups ?? [];
  const selectedGroup = scenarioGroups.find((group) => group.slug === selectedGroupSlug) ?? null;
  const stableSeed = selectedGroup ? buildScenarioFixtureSeed(selectedGroup.slug, profileSlug) : '';

  const handleSelectGroup = useCallback(
    (group: ScenarioGroupSummary) => {
      setSelectedGroupSlug(group.slug);
      setDocumentCount(group.default_documents);
      if (!fixtureName || fixtureName.startsWith('Test Persona')) {
        setFixtureName(`Test Persona - ${group.display_name}`);
      }
    },
    [fixtureName]
  );

  const handleCreate = useCallback(async () => {
    if (!fixtureName.trim() || !selectedGroup || !profileSlug) return;
    setIsCreating(true);
    setCreationError(null);
    setCreationErrorCode(undefined);
    notifiedFixtureSetIdRef.current = null;
    statusPollFailuresRef.current = 0;
    setCreationStatus({ pass: 0, passName: 'Creating fixture...', total: 4 });

    try {
      const resp = await createFromScenarioGroup({
        group_slug: selectedGroup.slug,
        profile_slug: profileSlug,
        seed: stableSeed,
        document_count: clampDocumentCount(documentCount, selectedGroup),
        name: fixtureName,
        description: fixtureDescription,
      }).unwrap();
      setJobID(resp.job_id);
    } catch (err) {
      setCreationError(extractFixtureCreationError(err));
      setCreationErrorCode(extractFixtureCreationErrorCode(err));
      setIsCreating(false);
    }
  }, [
    selectedGroup,
    profileSlug,
    stableSeed,
    documentCount,
    fixtureName,
    fixtureDescription,
    createFromScenarioGroup,
  ]);

  // Poll for job status
  useEffect(() => {
    if (!jobID) return;
    const interval = setInterval(async () => {
      try {
        const status = await fetchStatus(jobID).unwrap();
        statusPollFailuresRef.current = 0;
        setCreationStatus({
          pass: status.current_pass,
          passName: status.pass_name || 'Creating fixture...',
          total: status.total_passes,
        });
        if (status.status === 'completed') {
          clearInterval(interval);
          const fixtureSetId = status.result?.fixture_set_id;
          if (fixtureSetId && notifiedFixtureSetIdRef.current !== fixtureSetId) {
            notifiedFixtureSetIdRef.current = fixtureSetId;
            onCreated?.(fixtureSetId);
          }
          setCompletedSummary({
            fixtureSetId: fixtureSetId ?? '',
            sessionCount: status.result?.session_count ?? 0,
            scenarioGroupSlug: status.result?.scenario_group_slug,
            anonymizationStatus: status.result?.anonymization_status,
          });
          setIsCreating(false);
          setCompleted(true);
        } else if (status.status === 'failed') {
          clearInterval(interval);
          setCreationError(status.error || 'Fixture creation failed');
          setCreationErrorCode(status.error_code);
          setIsCreating(false);
        }
      } catch {
        statusPollFailuresRef.current += 1;
        if (statusPollFailuresRef.current < MAX_STATUS_POLL_FAILURES) return;
        clearInterval(interval);
        setCreationError('Job status unavailable. Please try again.');
        setCreationErrorCode('internal');
        setIsCreating(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [jobID, fetchStatus, onCreated]);

  const handleClose = useCallback(() => {
    setSelectedGroupSlug(null);
    setDocumentCount(0);
    setFixtureName('');
    setFixtureDescription('');
    setJobID(null);
    setIsCreating(false);
    setCreationStatus(null);
    setCreationError(null);
    setCreationErrorCode(undefined);
    setCompleted(false);
    setCompletedSummary(null);
    setShowAdvancedOptions(false);
    notifiedFixtureSetIdRef.current = null;
    statusPollFailuresRef.current = 0;
    onClose();
  }, [onClose]);

  const canCreate = Boolean(
    fixtureName.trim() &&
      selectedGroup &&
      profileSlug &&
      !isLoadingScenarioGroups &&
      !isScenarioGroupsError
  );
  const errorHelp = fixtureCreationFailureHelp(creationErrorCode);

  return (
    <Dialog open={open} onClose={isCreating ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Test Persona</DialogTitle>
      <DialogContent>
        {!isCreating && !completed && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
              A test persona is an anonymized bundle of real coaching-session context. Choose a
              scenario, and the system will select eligible sessions, validate transcript access,
              anonymize the content, then save a persona for prompt comparisons.
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Step 1: Choose a scenario
            </Typography>
            {!profileSlug && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Select a prompt before creating a scenario fixture.
              </Alert>
            )}
            {isLoadingScenarioGroups && <LinearProgress sx={{ mb: 2 }} />}
            {isScenarioGroupsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Scenario groups could not be loaded. Close and reopen this dialog, or refresh the
                page before creating a persona.
              </Alert>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                mb: 2,
              }}
            >
              {scenarioGroups.map((group) => {
                const compatible = isScenarioCompatible(group, profileSlug);
                return (
                  <Box
                    key={group.slug}
                    onClick={() => {
                      if (!compatible) return;
                      handleSelectGroup(group);
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: selectedGroupSlug === group.slug ? 'primary.main' : 'divider',
                      bgcolor: selectedGroupSlug === group.slug ? 'primary.50' : 'transparent',
                      cursor: compatible ? 'pointer' : 'not-allowed',
                      opacity: compatible ? 1 : 0.55,
                      transition: 'all 150ms',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {group.display_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.description}
                    </Typography>
                    {group.use_case && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {group.use_case}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                      {group.compatible_profiles.map((profile) => (
                        <Chip key={profile} label={profile} size="small" variant="outlined" />
                      ))}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      Builds a {group.default_documents}-session persona by default
                    </Typography>
                    {!compatible && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {group.unavailable_reason || 'Not available for this profile'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {selectedGroup && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Step 2: Name the persona
                </Typography>
                <TextField
                  label="Persona name"
                  size="small"
                  fullWidth
                  value={fixtureName}
                  onChange={(e) => setFixtureName(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  label="Description (optional)"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={fixtureDescription}
                  onChange={(e) => setFixtureDescription(e.target.value)}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowAdvancedOptions((value) => !value)}
                  sx={{ mt: 1.5, px: 0, textTransform: 'none' }}
                >
                  {showAdvancedOptions ? 'Hide Advanced options' : 'Advanced options'}
                </Button>
                <Collapse in={showAdvancedOptions}>
                  <TextField
                    label="Persona size"
                    size="small"
                    fullWidth
                    type="number"
                    value={documentCount}
                    onChange={(e) =>
                      setDocumentCount(clampDocumentCount(Number(e.target.value), selectedGroup))
                    }
                    inputProps={{
                      min: selectedGroup.min_documents,
                      max: selectedGroup.max_documents,
                    }}
                    helperText={`Minimum ${selectedGroup.min_documents}, maximum ${selectedGroup.max_documents}. Creation fails before anonymization if not enough candidates exist.`}
                    sx={{ mb: 1.5, mt: 1 }}
                  />
                  <TextField
                    label="Stable seed"
                    size="small"
                    fullWidth
                    value={stableSeed}
                    InputProps={{ readOnly: true }}
                    helperText="Same profile, scenario group, and seed resolve the same candidate order."
                  />
                </Collapse>
              </>
            )}
          </>
        )}

        {isCreating && creationStatus && (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" gutterBottom>
              Creating test persona... Stage {creationStatus.pass}/{creationStatus.total}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {creationStatus.passName || 'Creating test persona...'}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(creationStatus.pass / creationStatus.total) * 100}
            />
          </Box>
        )}

        {creationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">{creationError}</Typography>
            {errorHelp && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
                {errorHelp}
              </Typography>
            )}
          </Alert>
        )}

        {completed && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              Test persona created!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This persona has been selected in the Test column. You can run comparisons now.
            </Typography>
            {completedSummary && (
              <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
                {fixtureName || 'Test persona'} uses {completedSummary.sessionCount} anonymized
                sessions from {completedSummary.scenarioGroupSlug || selectedGroupSlug}.
                Anonymization {completedSummary.anonymizationStatus || 'passed'}.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {completed ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : !isCreating ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" disabled={!canCreate} onClick={handleCreate}>
              Create anonymized persona
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
